import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Loader2, RefreshCw, Plus, Lock, Globe, ArrowRight } from "lucide-react";
import { useGameInviteNotifications } from "../hooks/useGameInviteNotifications";

const TIME_OPTIONS = [
  { value: 180, label: "3 min" },
  { value: 300, label: "5 min" },
  { value: 600, label: "10 min" },
  { value: 900, label: "15 min" },
];

export default function OnlineRooms() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState(null);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(null);
  const [visibility, setVisibility] = useState("public");
  const [timeLimit, setTimeLimit] = useState(300);
  const navigate = useNavigate();
  useGameInviteNotifications();

  const fetchRooms = async () => {
    try {
      const { data } = await api.get("/games/online/rooms");
      setRooms(data);
    } catch (e) {
      setRooms([]);
    }
  };

  useEffect(() => {
    fetchRooms();
    const id = setInterval(fetchRooms, 5000);
    return () => clearInterval(id);
  }, []);

  const createRoom = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/games/online/create", { visibility, time_limit: timeLimit });
      if (visibility === "private") {
        const link = `${window.location.origin}/game/online/${data.room_id}`;
        try { await navigator.clipboard.writeText(link); } catch (_) { /* ignore */ }
        toast.success("Salle privée créée — lien copié dans le presse-papiers.");
      } else {
        toast.success("Salle créée !");
      }
      navigate(`/game/online/${data.room_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const joinRoom = async (roomId) => {
    setJoining(roomId);
    try {
      await api.post(`/games/online/join/${roomId}`);
      navigate(`/game/online/${roomId}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      fetchRooms();
    } finally {
      setJoining(null);
    }
  };

  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-10 fade-up">
        <p className="text-xs tracking-[0.3em] text-[color:var(--primary)] mb-2">JEU EN LIGNE</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">Salles ouvertes</h1>
        <p className="text-[color:var(--text-secondary)] text-sm mb-8">
          Rejoignez une salle ou créez la vôtre. Les salles privées ne sont accessibles que par lien d'invitation.
        </p>

        {/* Create room */}
        <div className="glass rounded-lg p-6 mb-8" data-testid="create-room-panel">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Plus size={16} className="text-[color:var(--primary)]" /> Créer une salle</h2>
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs tracking-[0.2em] text-[color:var(--text-secondary)] mb-2">VISIBILITÉ</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setVisibility("public")}
                  className={`flex-1 px-3 py-2 rounded-md border text-xs flex items-center justify-center gap-2 ${visibility === "public" ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                  data-testid="visibility-public"
                >
                  <Globe size={14} /> Publique
                </button>
                <button
                  onClick={() => setVisibility("private")}
                  className={`flex-1 px-3 py-2 rounded-md border text-xs flex items-center justify-center gap-2 ${visibility === "private" ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                  data-testid="visibility-private"
                >
                  <Lock size={14} /> Privée
                </button>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs tracking-[0.2em] text-[color:var(--text-secondary)] mb-2">TEMPS PAR JOUEUR</label>
              <div className="flex flex-wrap gap-2">
                {TIME_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTimeLimit(opt.value)}
                    className={`px-3 py-2 rounded-md border text-xs ${timeLimit === opt.value ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                    data-testid={`time-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={createRoom}
            disabled={creating}
            data-testid="create-room-submit"
            className="btn-gold px-5 py-2.5 rounded-md text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {creating && <Loader2 size={14} className="animate-spin" />}
            Créer la salle <ArrowRight size={14} />
          </button>
        </div>

        {/* Rooms list */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Salles publiques disponibles</h2>
          <button onClick={fetchRooms} className="text-xs flex items-center gap-1.5 text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]" data-testid="refresh-rooms">
            <RefreshCw size={14} /> Actualiser
          </button>
        </div>

        <div className="glass rounded-lg overflow-hidden" data-testid="rooms-list">
          {rooms === null && (
            <div className="px-5 py-10 text-center text-sm flex items-center justify-center gap-2 text-[color:var(--text-secondary)]">
              <Loader2 className="animate-spin" size={16} /> Chargement…
            </div>
          )}
          {rooms && rooms.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-[color:var(--text-secondary)]">
              Aucune salle ouverte. Créez-en une !
            </div>
          )}
          {rooms?.map((r) => {
            const mine = r.host_id === user?.id;
            return (
              <div
                key={r.room_id}
                className="grid grid-cols-[1fr_80px_140px] gap-3 px-5 py-4 border-b border-[color:var(--border)] last:border-0 text-sm items-center"
                data-testid={`room-${r.room_id}`}
              >
                <div>
                  <div className="font-semibold">{r.host}{mine && <span className="ml-2 text-xs text-[color:var(--text-secondary)]">(vous)</span>}</div>
                  <div className="text-xs text-[color:var(--text-secondary)]">Créée {new Date(r.created_at).toLocaleTimeString("fr-FR")}</div>
                </div>
                <div className="text-xs text-center text-[color:var(--text-secondary)]">{Math.round(r.time_limit / 60)} min</div>
                <button
                  onClick={() => joinRoom(r.room_id)}
                  disabled={joining === r.room_id}
                  className="btn-gold px-4 py-2 rounded-md text-xs flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid={`join-room-${r.room_id}`}
                >
                  {joining === r.room_id ? <Loader2 size={12} className="animate-spin" /> : (mine ? "Rejoindre" : "Affronter")}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-6">
          <Link to="/lobby" className="text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]">
            ← Retour au lobby
          </Link>
        </div>
      </main>
    </div>
  );
}
